import React from 'react';
import moment from 'moment';
import { FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem } from '@mui/material';
import { isEqual } from 'lodash';

import Checkbox from '../../../../../../common/components/Checkbox/Checkbox';
import TextField from '../../../../../../common/components/TextField/TextField';
import Select from '../../../../../../common/components/Select/Select';
import ScheduleField from './ScheduleField';
import { getCalculatedSourceValue, getMultiplySourceValue, getSourceValue, getStateValue, getValue } from '../helper';
import ChargerCombobox from '../../../../../../common/components/Form/ChargerCombobox';
import { makeNodeName } from '../../helper';
import { doesCountryUse12HoursFormat } from '../../../../../../common/constants/location';
import { TimePicker } from '../../../../../../common/components/DateTimePickers/DateTimePickers';

const FIELD_BUILDER_MAP = {
  text: (params) => {
    const controlled = getValue(params, 'field.controlled');

    return !getValue(params, 'field.ignore') ? (
      <TextField
        style={getValue(params, 'field.style')}
        label={getValue(params, 'field.label')}
        name={getValue(params, 'field.name')}
        required={getValue(params, 'field.required')}
        disabled={getValue(params, 'field.disabled') || getValue(params, 'locked')}
        hidden={getValue(params, 'field.hidden')}
        helperText={getValue(params, 'field.helperText')}
        error={!!getValue(params, 'field.error')}
        errorText={getValue(params, 'field.error')}
        value={controlled ? getSourceValue(params) : undefined}
        defaultValue={!controlled ? getSourceValue(params) : undefined}
        InputProps={{
          readOnly: getValue(params, 'field.readOnly'),
        }}
        nohovericon={getValue(params, 'field.noHoverIcon')}
        onChange={params.onChange}
      />
    ) : null;
  },
  number: (params) => {
    const controlled = getValue(params, 'field.controlled');
    const calculate = getValue(params, 'field.calculate');
    const recalculate = getValue(params, 'field.recalculate');
    let recalculateItem = null;

    if (recalculate) {
      recalculateItem = document.getElementsByName(recalculate)[0];
    }

    const handleOnChange = (event) => {
      const dependValue = {};

      if (recalculate) {
        dependValue.name = recalculate;
        const value = getCalculatedSourceValue({
          ...params,
          valueForUpdate: event.target.value,
        });

        dependValue.value = value;

        if (recalculateItem) {
          recalculateItem.value = value;
        }
      }

      params.onChange(event, dependValue);
    };

    const getFieldValue = calculate ? getCalculatedSourceValue : getSourceValue;
    const getDefaultFieldValue = calculate ? getCalculatedSourceValue : getMultiplySourceValue;

    return !getValue(params, 'field.ignore') ? (
      <TextField
        type="number"
        style={getValue(params, 'field.style')}
        label={getValue(params, 'field.label')}
        name={getValue(params, 'field.name')}
        required={getValue(params, 'field.required')}
        disabled={getValue(params, 'field.disabled') || getValue(params, 'locked')}
        helperText={getValue(params, 'field.helperText')}
        error={!!getValue(params, 'field.error')}
        errorText={getValue(params, 'field.error')}
        value={controlled ? getFieldValue(params) : undefined}
        defaultValue={!controlled ? getDefaultFieldValue(params) : undefined}
        inputProps={{
          min: getValue(params, 'field.min'),
          max: getValue(params, 'field.max'),
          className: getValue(params, 'field.className'),
        }}
        InputProps={{
          readOnly: getValue(params, 'field.readOnly'),
        }}
        onChange={handleOnChange}
        nohovericon={getValue(params, 'field.noHoverIcon')}
      />
    ) : null;
  },
  select: (params) => (
    <FormControl
      required={getValue(params, 'field.required')}
      error={!!getValue(params, 'field.error')}
      style={getValue(params, 'field.style')}
    >
      <InputLabel shrink>{getValue(params, 'field.label')}</InputLabel>
      <Select
        name={getValue(params, 'field.name')}
        defaultValue={getSourceValue(params)}
        disabled={getValue(params, 'field.disabled') || getValue(params, 'locked')}
        onChange={params.onChange}
      >
        {getValue(params, 'field.options').map((option) => (
          <MenuItem disabled={option.disabled} key={option.value} value={option.value} title={option.label}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{getValue(params, 'field.error') || getValue(params, 'field.helperText')}</FormHelperText>
    </FormControl>
  ),
  timeFrom: (params) => (
    <TimePicker
      ampm={doesCountryUse12HoursFormat()}
      name={getValue(params, 'field.name')}
      label={getValue(params, 'field.label')}
      disabled={getValue(params, 'field.disabled') || getValue(params, 'locked')}
      minutesStep={getValue(params, 'field.minutesStep')}
      value={moment(0).add(getStateValue(params), 'seconds').utc()}
      slotProps={{ textField: { readOnly: true, style: getValue(params, 'field.style') } }}
      onAccept={(value) => {
        params.onChange({
          target: {
            type: 'timeFrom',
            name: getValue(params, 'field.name'),
            value,
          },
        });
      }}
    />
  ),
  timeTo: (params) => {
    let value = getStateValue(params);

    value = value === 86399 ? 86400 : value;

    return (
      <TimePicker
        ampm={doesCountryUse12HoursFormat()}
        name={getValue(params, 'field.name')}
        label={getValue(params, 'field.label')}
        disabled={getValue(params, 'field.disabled') || getValue(params, 'locked')}
        minutesStep={getValue(params, 'field.minutesStep')}
        value={moment(0).add(value, 'seconds').utc()}
        slotProps={{ textField: { readOnly: true, style: getValue(params, 'field.style') } }}
        onAccept={(value) => {
          params.onChange({
            target: {
              type: 'timeTo',
              name: getValue(params, 'field.name'),
              value,
            },
          });
        }}
      />
    );
  },
  checkbox: (params) => (
    <FormControlLabel
      style={getValue(params, 'field.style')}
      label={getValue(params, 'field.label')}
      control={
        <Checkbox
          defaultChecked={getSourceValue(params)}
          required={getValue(params, 'field.required')}
          disabled={getValue(params, 'field.disabled') || getValue(params, 'locked')}
          name={getValue(params, 'field.name')}
          color="primary"
          onChange={params.onChange}
        />
      }
    />
  ),
  schedule: (params) => <ScheduleField {...params} builderMap={FIELD_BUILDER_MAP} />,
  block: ({ field: { style } }) => <div style={style} />,
  charger: (params) => (
    <ChargerCombobox
      plazaId={getValue(params, 'plazaId')}
      label={getValue(params, 'field.label')}
      name={getValue(params, 'field.name')}
      required={getValue(params, 'field.required')}
      style={getValue(params, 'field.style')}
      error={!!getValue(params, 'field.error')}
      errorText={getValue(params, 'field.error')}
      defaultValue={getSourceValue(params)}
      excludedChargers={getValue(params, 'excludedChargers')}
      includedChargers={getValue(params, 'includedChargers')}
      onChange={(_, option) => {
        let staticSection;

        if (option) {
          staticSection = {
            ...params.formState.staticSection,
            chargerUuid: option.value,
            nodeName: option.label,
          };
          params.setState('formState', {
            ...params.formState,
            isDirty: !isEqual(staticSection, params.initialState),
            staticSection,
          });
        } else {
          staticSection = {
            ...params.formState.staticSection,
            chargerUuid: null,
            condition: null,
            nodeName: makeNodeName({
              nodeType: params.node.topologyNodeType,
              nodes: params.nodes,
            }),
          };
          params.setState('formState', {
            ...params.formState,
            isDirty: !isEqual(staticSection, params.initialState),
            staticSection,
          });
        }
      }}
    />
  ),
  hidden: () => null,
};

export default FIELD_BUILDER_MAP;
